// Every GraphQL error is reported from the single `errorLink` in
// `@/utils/apolloClient`, so Sentry's stacktrace-based grouping collapses all of
// them — unrelated bugs across unrelated operations — into one issue. This is
// the GraphQL counterpart to `sentryAxios`'s endpoint + status fingerprint.

/**
 * Error codes that are a normal answer for an operation rather than a bug,
 * keyed by operation name.
 *
 * The `user` query is a lookup by email/id that the platform-member, coach,
 * club-management, parent and partner forms use to probe whether an account
 * already exists. A `NOT_FOUND` is the expected "no such user" reply and every
 * caller handles it (toast / "user is not in the list" branch), so reporting it
 * buried the real errors under thousands of Sentry events.
 */
const EXPECTED_GRAPHQL_ERROR_CODES: Record<string, readonly string[]> = {
  user: ["NOT_FOUND"],
};

/** Whether this operation/code pair is an expected outcome and must not be reported. */
export const isExpectedGraphQLError = (
  operationName: string | null | undefined,
  code: string | undefined
): boolean =>
  !!operationName && !!code && !!EXPECTED_GRAPHQL_ERROR_CODES[operationName]?.includes(code);

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const PUBLIC_ID_PATTERN = /\b[a-z]{2,5}_[A-Za-z0-9]{6,}\b/g;
const NUMBER_PATTERN = /\d+/g;

// Collapse the ids backend messages embed so "Team 12 not found" and
// "Team 34 not found" stay one issue instead of one issue per record.
const normalizeMessage = (message: string): string =>
  message
    .replace(UUID_PATTERN, "*")
    .replace(PUBLIC_ID_PATTERN, "*")
    .replace(NUMBER_PATTERN, "*")
    .trim();

/**
 * Fingerprint a GraphQL error by operation + error code + the shape of its
 * message, so each distinct failure gets its own Sentry issue.
 */
export const getGraphQLErrorFingerprint = (
  operationName: string | null | undefined,
  code: string | undefined,
  message: string
): string[] => [
  "graphql",
  operationName || "anonymous",
  code ?? "unknown",
  normalizeMessage(message),
];

/** The operation-level cause pulled out of an Apollo `ServerError`'s response body. */
export type ServerErrorInfo = {
  statusCode?: number;
  code?: string;
  message?: string;
};

/** Minimal shape of the GraphQL errors the backend embeds in a non-2xx body. */
type ResponseBodyError = { message?: unknown; extensions?: { code?: unknown } };

/**
 * A GraphQL request that gets a non-2xx HTTP response (a 400 validation error, a
 * 500, …) surfaces through `errorLink` as a `networkError` — an Apollo
 * `ServerError` — NOT through `graphQLErrors`. That path bypasses
 * {@link getGraphQLErrorFingerprint}, so Sentry falls back to grouping every
 * operation's 4xx/5xx under the shared `throwServerError` stacktrace (one
 * catch-all issue with no visible cause).
 *
 * Pull the status plus the first GraphQL error's code/message out of the
 * ServerError's parsed body (`error.result.errors`) so the network path can be
 * grouped and suppressed the same way. Returns `{}` for anything that isn't a
 * recognisable ServerError (e.g. a real offline `TypeError: Failed to fetch`).
 */
export const extractServerErrorInfo = (networkError: unknown): ServerErrorInfo => {
  if (!networkError || typeof networkError !== "object") return {};

  const err = networkError as { statusCode?: unknown; result?: unknown };
  const statusCode = typeof err.statusCode === "number" ? err.statusCode : undefined;

  const errors =
    err.result && typeof err.result === "object"
      ? (err.result as { errors?: unknown }).errors
      : undefined;
  const firstError = Array.isArray(errors)
    ? (errors[0] as ResponseBodyError | undefined)
    : undefined;

  const code =
    typeof firstError?.extensions?.code === "string" ? firstError.extensions.code : undefined;
  const message = typeof firstError?.message === "string" ? firstError.message : undefined;

  return { statusCode, code, message };
};

/**
 * Fingerprint a GraphQL *network* error (a non-2xx HTTP response) by operation +
 * status + the response's error code/message, so each distinct 4xx/5xx failure
 * becomes its own Sentry issue instead of collapsing into the shared
 * `ServerError` stacktrace group. The `graphql-network` prefix and the status
 * segment keep it distinct from the `graphQLErrors` fingerprint above.
 */
export const getServerErrorFingerprint = (
  operationName: string | null | undefined,
  { statusCode, code, message }: ServerErrorInfo
): string[] => [
  "graphql-network",
  operationName || "anonymous",
  statusCode != null ? String(statusCode) : "unknown",
  code ?? "unknown",
  message ? normalizeMessage(message) : "unknown",
];
